import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  LOCALE_ID,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { Router, ActivatedRoute, Data } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import {
  MatLegacyTable as MatTable,
  MatLegacyTableDataSource as MatTableDataSource,
} from '@angular/material/legacy-table';
import { MatLegacyTabGroup as MatTabGroup } from '@angular/material/legacy-tabs';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatDrawer } from '@angular/material/sidenav';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';

import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { Observable, filter, finalize, map, noop, of, switchMap, take, tap, zip } from 'rxjs';

import { AccountService } from 'app/core';
import { FieldDef, User } from 'app/core/models';
import { LayoutService } from 'app/layout/layout.service';
import { ProjectTemplate } from 'app/modules/projects/projects.types';
import { Column, ColumnConfig, FieldCustom } from 'app/shared/modules/legacy-tasks/list/list.types';
import { ReadableFieldPipe } from 'app/shared/pipes/readable-field.pipe';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { LegacyTasksTabService } from '@shared/modules/legacy-tasks/services/tasks.service';
import { defaultTaskTableFields } from 'app/shared/modules/legacy-tasks/list/list.constants';
import { SelectableFieldDef } from 'app/shared/components/column-selector/column-selector.types';
import {
  Task,
  TaskList,
  TaskListTemplate,
  TaskStatus,
  TaskType,
  WorkflowState,
} from 'app/shared/modules/legacy-tasks/tasks.types';
import { ConfirmService } from 'app/shared/components/widgets/dialog/confirm/confirm.service';
import { Select, Store } from '@ngxs/store';
import { ObjectsManagerState } from '../../objects-manager/state/state';
import { CustomObject } from 'app/shared/modules/custom-objects/custom-objects.types';
import { ObjectsManagerActions } from '../../objects-manager/state/actions';

@Component({
  selector: 'settings-layouts-details',
  templateUrl: './details.component.html',
  styleUrls: ['./details.component.scss', './../../../../shared/modules/legacy-tasks/list/table/table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SettingsLayoutsDetailsComponent extends OnDestroyMixin implements OnInit, OnDestroy {
  public projectTemplate: ProjectTemplate<any>;
  public projectTemplateVersionsCount: number;

  public projectTemplates: Array<ProjectTemplate<any>>;
  public projectTemplateVersions: any[];
  public projectTemplateVersionsTableColumns: string[] = ['id', 'event', 'user', 'created_at', 'actions'];
  public dataSource: MatTableDataSource<ProjectTemplate<any>>;
  private sortApplied: boolean = false;

  @ViewChildren('childTable') matTables: QueryList<MatTable<any>>;
  @ViewChild('matDrawer', { static: true }) matDrawer: MatDrawer;
  @ViewChild(MatSort) private sort: MatSort;

  constructor(
    @Inject(LOCALE_ID) public locale: string,
    private readonly router: Router,
    private readonly snackBar: MatSnackBar,
    private readonly matDialog: MatDialog,
    private readonly cdRef: ChangeDetectorRef,
    private _activatedRoute: ActivatedRoute,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly accountService: AccountService,
    private readonly projectsService: ProjectsService,
    private readonly tasksTabService: LegacyTasksTabService,
    private readonly layoutService: LayoutService,
    private readonly confirmService: ConfirmService,
    private readonly store: Store,
  ) {
    super();
  }

  ngOnInit(): void {
    this._activatedRoute.data.pipe(untilComponentDestroyed(this)).subscribe((data: Data) => {
      this.projectTemplate = data.projectTemplate;
      this.getProjectTemplateVersions();
    });
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  getProjectTemplateVersions(): void {
    this.projectsService
      .getProjectTemplateVersions(this.projectTemplate.id)
      .pipe(untilComponentDestroyed(this))
      .subscribe((versions) => {
        this.projectTemplateVersions = versions.sort((a, b) => b.id - a.id);
        this.dataSource = new MatTableDataSource<any>(this.projectTemplateVersions);
        this.projectTemplateVersionsCount = this.projectTemplateVersions.length;
        this.cdRef.markForCheck();
      });
  }

  restoreVersion(projectTemplateVersionId) {
    this.confirmService
      .openConfirmationModal({
        message: `Are you sure you wish to restore this version of the layout? Please note that this will replace any place where this layout is in use.`,
      })
      .pipe(
        filter((result: boolean) => result),
        untilComponentDestroyed(this),
      )
      .subscribe(() => {
        this.projectsService
          .restoreProjectTemplateVersion(this.projectTemplate.id, projectTemplateVersionId)
          .pipe(untilComponentDestroyed(this))
          .subscribe(() => {
            this.getProjectTemplateVersions();
            this.snackBar.open('Layout restored successfully', 'Close', { duration: 2000 });
          });
      });
  }

  ngAfterViewChecked(): void {
    this.applySortToDataSource();
  }

  // Check if the MatSort has been applied and apply it if not
  public applySortToDataSource(): void {
    if (this.dataSource && this.sort && !this.sortApplied) {
      this.dataSource.sort = this.sort;
      this.sortApplied = true;
    }
  }

  public trackByFn(index: number, item: TaskListTemplate): number {
    return item.id || index;
  }

  public onDrawerClosed(): void {
    return this.layoutService.emitCommon('openDrawer', false, 'subject');
  }
}
